import { useCallback } from 'react';
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';

import { useApiQuery } from '../useApiQuery';
import { reviewKeys } from '@/lib/queryKeys';
import { reviewsV2Endpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import { shouldRetry } from '@/lib/queryClient';
import {
  flattenStoryPages,
  isRejectedStoryCursor,
} from '@/components/organisms/reviews-dashboard/story-pagination';
import { isBadRequest } from '@/lib/api/errors';
import type {
  ReviewStoryApiResponse,
  ReviewStoryListResponse,
  UnifiedReviewListParams,
} from '@/types/review';

export function useReviewStories(params: UnifiedReviewListParams = {}) {
  return useApiQuery<ReviewStoryListResponse>({
    queryKey: [...reviewKeys.lists(), 'stories', params as Record<string, unknown>],
    queryFn: async ({ signal }) => {
      const raw = await apiGet<ReviewStoryApiResponse>(
        operationsClient,
        reviewsV2Endpoints.stories(),
        {
          params: serializeParams({ ...params, limit: params.limit ?? 100 }),
          signal,
        },
      );

      return { items: raw.data ?? [], nextCursor: raw.nextCursor };
    },
    // A refused cursor is permanent: the same request produces the same 400
    // however often it is repeated. Left alone, the poll would re-issue it
    // every 30s forever and each round would burn the default retries on top —
    // traffic that cannot succeed, and an error state the operator can never
    // clear. Stop both, and let the pager offer the first page instead.
    refetchInterval: (query) =>
      isRejectedStoryCursor(query.state.error, params.cursor) ? false : 30_000,
    retry: (failureCount, error) =>
      isRejectedStoryCursor(error, params.cursor) ? false : shouldRetry(failureCount, error),
    refetchOnWindowFocus: false,
    showErrorToast: false,
  });
}

function serializeParams(params: UnifiedReviewListParams): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null || value === '') return false;
      return !Array.isArray(value) || value.length > 0;
    }),
  );
}

/**
 * The stories list, with every held page refetched together.
 *
 * `useReviewStories` keys on the cursor, so the component had to hold pages in
 * its own state and only the ACTIVE cursor ever refreshed. Page 1 then sat
 * frozen while the 30s poll refreshed page 2: a story arriving at the top never
 * appeared, and the boundary between two pages fetched from different snapshots
 * could duplicate or skip one.
 *
 * That was survivable while reaching page 2 took a deliberate press. Adding
 * scroll-to-load made it the ordinary path, so the pages have to move together.
 * An infinite query refetches all of them on the same tick, which is the same
 * answer the table reached in `useInfiniteUnifiedReviews`.
 */
/** The cache key for one stories list. */
function reviewStoriesInfiniteKey(params: Omit<UnifiedReviewListParams, 'cursor'>) {
  return [...reviewKeys.lists(), 'stories', 'infinite', params as Record<string, unknown>];
}

export function useInfiniteReviewStories(params: UnifiedReviewListParams = {}) {
  const { cursor: _ignoredCursor, ...rest } = params;
  const queryClient = useQueryClient();
  const queryKey = reviewStoriesInfiniteKey(rest);
  const query = useInfiniteQuery({
    // The cursor names a page within this list, not a different list.
    queryKey,
    queryFn: async ({
      pageParam,
      signal,
    }: {
      pageParam: string | undefined;
      signal?: AbortSignal;
    }) => {
      const raw = await apiGet<ReviewStoryApiResponse>(
        operationsClient,
        reviewsV2Endpoints.stories(),
        {
          params: serializeParams({ ...rest, cursor: pageParam, limit: rest.limit ?? 100 }),
          signal,
        },
      );
      return { items: raw.data ?? [], nextCursor: raw.nextCursor };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: ReviewStoryListResponse) => last.nextCursor ?? undefined,
    // A 400 here is a verdict, not a hiccup: the service answers one for a
    // cursor it cannot resolve, and re-issuing it produces the same 400
    // forever. Polling it would burn a request every 30s and leave an error
    // the operator cannot clear; retrying it burns the retries on top.
    //
    // Wider than the old `isRejectedStoryCursor` check, which only stopped a
    // 400 that CARRIED a cursor and let a first-page 400 fall through to the
    // transient path. An identical request cannot argue its way out of a 400
    // whether or not a cursor was involved, so retrying one was never useful.
    refetchInterval: (query) => (isBadRequest(query.state.error) ? false : 30_000),
    meta: { suppressToast: true },
    refetchOnWindowFocus: false,
    select: selectStoryPages,
  });

  /**
   * Recovery for a cursor the server refuses.
   *
   * Discards the held pages rather than refetching them: `refetch()` would
   * re-issue every cursor already used, and one of them is the one that was
   * just rejected. Resetting starts again from the first page, which is the one
   * page no cursor rejection can apply to.
   *
   * It lives in the hook so the component needs no query client of its own —
   * which also means the specs that mock this hook do not have to stand up a
   * provider to render the view.
   */
  const resetToFirstPage = useCallback(() => {
    // Drop the later pages, keep the first, then refetch it.
    //
    // NOT `resetQueries`: that returns the query to its pre-loaded state, so
    // `data` goes undefined and the view blanks until the refetch lands — on a
    // slow connection the recovery action looks like the list being wiped. The
    // page we already hold is exactly what should stay on screen while the
    // refresh happens.
    //
    // The later pages go because their cursors came from a window the server
    // has just told us it can no longer resolve.
    queryClient.setQueryData<InfiniteData<ReviewStoryListResponse, string | undefined>>(
      queryKey,
      (held) =>
        held && held.pages.length > 1
          ? { pages: held.pages.slice(0, 1), pageParams: held.pageParams.slice(0, 1) }
          : held,
    );
    void queryClient.invalidateQueries({ queryKey });
    // `queryKey` is rebuilt each render from `rest`; its CONTENT is what
    // matters, so depend on the serialized form rather than the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, JSON.stringify(queryKey)]);

  return { ...query, resetToFirstPage };
}

/**
 * Declared once rather than inline: React Query caches a select result against
 * the function identity it came from, so a new closure each render throws it
 * away.
 */
export function selectStoryPages(data: { pages: ReviewStoryListResponse[] }) {
  return {
    items: flattenStoryPages(data.pages),
    // The newest page owns "is there more". Earlier ones all carried a cursor
    // and would claim there is.
    nextCursor: data.pages[data.pages.length - 1]?.nextCursor,
  };
}
